/**
 * Tam-özellikli oturum sayfası.
 *
 * Ekran görüntüleme (H264 WebCodecs) + ses (Opus WebCodecs) + pano + dosya
 * transferi + input (klavye/fare) + sohbet + sistem aksiyonları + gizlilik
 * perdesi + sağlık rozeti + kayıt (MediaRecorder).
 */
import { H264Decoder } from '../services/video-decoder';
import { WsBridge } from '../services/ws-bridge';
import { VideoReassembler, decodeHeader, Channel } from '../services/packet-reassembler';
import { InputSender, VK } from '../services/input-sender';
import { AudioPlayer, parseAudioFrame } from '../services/audio-player';
import { ClipboardBroker } from '../services/clipboard-broker';
import { FileSender } from '../services/file-sender';
import { SignalingClient } from '../services/signaling-client';
import { ensureWebControllerDevice } from '../services/web-controller-device';
import { logEvent } from '../services/web-diagnostics';
import { activeSession } from '../services/active-session';
import { appState } from '../state';
export function renderSession(pairing, jwt, relayUrl = 'wss://relay.photondownload.com', signalingUrl = 'wss://signal.photondownload.com/ws') {
    const root = document.createElement('div');
    root.style.cssText = 'font-family:var(--font-sans);display:flex;flex-direction:column;height:100vh;margin:0;background:var(--bg-base);color:var(--text-primary)';
    root.innerHTML = `
    <div id="toolbar" style="display:flex;gap:6px;padding:8px 12px;background:var(--bg-elev-1);color:var(--text-primary);align-items:center;flex-wrap:wrap;border-bottom:1px solid var(--border-subtle)">
      <button id="btn-close" style="padding:6px 12px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer;font-family:inherit">← Çıkış</button>
      <span style="color:var(--text-tertiary)">│</span>
      <select id="sel-quality" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);font-family:inherit" title="Video kalitesi">
        <option value="auto">Otomatik</option>
        <option value="high">Yüksek 4Mbps</option>
        <option value="balanced">Dengeli 1.5Mbps</option>
        <option value="smooth">Akıcı 600kbps</option>
      </select>
      <button id="btn-cad" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="Ctrl+Alt+Del">⌃⌥⌦</button>
      <button id="btn-lock" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="Kilitle">🔒</button>
      <button id="btn-mute" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="Ses aç/kapa">🔇</button>
      <button id="btn-curtain" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="Siyah ekran (gizlilik)">🌙</button>
      <button id="btn-stats" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="İstatistik göster/gizle">📊</button>
      <button id="btn-chat" title="Sohbet" style="position:relative;padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer">💬<span id="chat-unread-badge" style="display:none;position:absolute;top:-4px;right:-4px;min-width:16px;height:16px;padding:0 4px;font-size:10px;font-weight:bold;background:var(--status-error);color:#fff;border-radius:8px;line-height:16px;text-align:center">0</span></button>
      <button id="btn-keyboard" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="Klavyeyi aç (dokunmatik cihazlar)">⌨</button>
      <button id="btn-reconnect" title="Yeniden bağlan" style="display:none;padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer">🔄 Yeniden Bağlan</button>
      <button id="btn-record" style="padding:6px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);border-radius:var(--radius-sm);color:var(--text-primary);cursor:pointer" title="Oturumu kaydet">● Kayıt</button>
      <div style="flex:1"></div>
      <span id="relay-badge" title="Faz V web transport" style="font-size:11px;font-family:var(--font-mono);color:var(--accent);background:var(--accent-soft);border:1px solid var(--accent);padding:3px 8px;border-radius:var(--radius-pill);letter-spacing:0.04em">RELAY #${pairing.relaySessionId.toString(16).padStart(6, '0').toUpperCase()}</span>
      <span id="health-badge" title="Bağlantı sağlığı">🟡</span>
      <span id="bitrate" style="font-size:12px;font-family:var(--font-mono);color:var(--text-secondary)">— Mbps</span>
      <span id="session-label" style="font-size:11px;font-family:var(--font-mono);color:var(--text-tertiary);letter-spacing:0.06em">${pairing.hostHumanId}</span>
    </div>
    <div id="stats" style="padding:4px 12px;background:var(--bg-base);color:var(--text-tertiary);font:11px var(--font-mono);border-bottom:1px solid var(--border-subtle)"></div>
    <div id="main" style="flex:1;display:flex;overflow:hidden;background:#000">
      <div id="viewport" style="flex:1;position:relative;overflow:hidden">
        <canvas id="video" tabindex="0" style="width:100%;height:100%;outline:none;cursor:crosshair" width="1920" height="1080"></canvas>
        <!-- Faz V U8: mobil klavye — dokunmatik cihazlarda klavye butonu bu hidden input'a focus eder, iOS/Android otomatik klavye açılır. Input'a gelen key event'leri canvas keydown/keyup'a delege ediyoruz. -->
        <input id="kbd-capture" type="text" autocomplete="off" autocorrect="off" autocapitalize="none" spellcheck="false" style="position:absolute;left:-9999px;top:0;width:1px;height:1px;opacity:0" />
        <div id="error-overlay" style="position:absolute;top:0;left:0;right:0;bottom:0;display:none;background:rgba(10,14,26,0.92);color:var(--text-primary);padding:32px;font-size:14px;line-height:1.6;overflow-y:auto;z-index:10"></div>
      </div>
      <div id="chat-panel" style="display:none;width:340px;background:var(--bg-elev-1);color:var(--text-primary);flex-direction:column;border-left:1px solid var(--border-subtle);position:relative">
        <div style="display:flex;align-items:center;gap:10px;padding:12px 14px;background:var(--bg-elev-2);border-bottom:1px solid var(--border-subtle)">
          <div style="position:relative;width:36px;height:36px;flex-shrink:0">
            <div style="width:36px;height:36px;border-radius:50%;background:var(--accent-soft);display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;color:var(--accent);border:1px solid var(--accent)" id="chat-peer-initial">H</div>
            <div style="position:absolute;bottom:-1px;right:-1px;width:10px;height:10px;background:var(--status-online);border:2px solid var(--bg-elev-2);border-radius:50%"></div>
          </div>
          <div style="flex:1;min-width:0">
            <div id="chat-peer-name" style="font-size:13px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;color:var(--text-primary)">Uzak Bilgisayar</div>
            <div id="chat-peer-status" style="font-size:10px;color:var(--status-online);letter-spacing:0.04em">çevrimiçi · oturum aktif</div>
          </div>
          <button id="btn-chat-close" title="Sohbeti gizle" style="width:28px;height:28px;padding:0;background:transparent;border:none;color:var(--text-tertiary);cursor:pointer;font-size:18px;border-radius:var(--radius-sm)">×</button>
        </div>
        <div id="chat-log" style="flex:1;overflow-y:auto;padding:10px 12px;font-size:13px;display:flex;flex-direction:column;gap:2px"></div>
        <div id="chat-drop-hint" style="display:none;position:absolute;left:12px;right:12px;top:62px;bottom:80px;background:var(--accent-soft);border:2px dashed var(--accent);border-radius:var(--radius-md);align-items:center;justify-content:center;flex-direction:column;gap:6px;color:var(--accent);font-weight:600;pointer-events:none;z-index:5">
          <div style="font-size:32px">📥</div>
          <div>Dosyayı bırak — gönderilecek</div>
        </div>
        <form id="chat-form" style="display:flex;gap:6px;padding:10px 12px;background:var(--bg-elev-2);border-top:1px solid var(--border-subtle);align-items:flex-end">
          <label id="chat-attach-btn" title="Dosya ekle (sürükle-bırak da yapabilirsin)" style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;cursor:pointer;background:var(--bg-elev-3);border:1px solid var(--border-subtle);border-radius:var(--radius-sm);color:var(--text-secondary);font-size:18px;flex-shrink:0">
            📎<input type="file" id="file-input" style="display:none">
          </label>
          <textarea id="chat-input" rows="1" placeholder="Mesaj yaz…" style="flex:1;min-height:36px;max-height:120px;padding:8px 10px;background:var(--bg-elev-3);border:1px solid var(--border-strong);color:var(--text-primary);border-radius:var(--radius-sm);font-family:inherit;font-size:13px;resize:none;outline:none"></textarea>
          <button type="submit" title="Gönder (Enter)" style="width:36px;height:36px;padding:0;background:var(--accent);border:none;color:var(--on-accent);border-radius:var(--radius-sm);cursor:pointer;font-size:16px;font-weight:700;flex-shrink:0">➤</button>
        </form>
      </div>
    </div>
  `;
    const canvas = root.querySelector('#video');
    const ctx = canvas.getContext('2d');
    const stats = root.querySelector('#stats');
    const healthBadge = root.querySelector('#health-badge');
    const bitrateSpan = root.querySelector('#bitrate');
    const chatPanel = root.querySelector('#chat-panel');
    const chatLog = root.querySelector('#chat-log');
    const chatDropHint = root.querySelector('#chat-drop-hint');
    const chatUnreadBadge = root.querySelector('#chat-unread-badge');
    const errorOverlay = root.querySelector('#error-overlay');
    let chatUnreadCount = 0;
    let chatLastDay = null;
    let chatLastSender = null;
    function setUnreadBadge(n) {
        chatUnreadCount = n;
        if (n <= 0) {
            chatUnreadBadge.style.display = 'none';
        }
        else {
            chatUnreadBadge.style.display = 'inline-block';
            chatUnreadBadge.textContent = n > 99 ? '99+' : String(n);
        }
    }
    // Faz V: backend pairing'i host display name'i hostHumanId olarak veriyor.
    const peerNameEl = root.querySelector('#chat-peer-name');
    const peerInitialEl = root.querySelector('#chat-peer-initial');
    if (pairing.hostHumanId) {
        peerNameEl.textContent = pairing.hostHumanId;
        peerInitialEl.textContent = pairing.hostHumanId.charAt(0).toUpperCase();
    }
    function showError(msg) {
        logEvent('error', `session.showError: ${msg}`);
        // B4 #21: innerHTML + manuel encoding hata-prone (& mismatch tehlikesi).
        // DOM API + textContent → XSS-safe by construction.
        errorOverlay.replaceChildren();
        const wrap = document.createElement('div');
        wrap.style.cssText = 'max-width:640px;margin:40px auto';
        const h = document.createElement('h2');
        h.style.color = '#f55';
        h.textContent = '⚠ Bağlantı hatası';
        const p = document.createElement('p');
        p.style.whiteSpace = 'pre-wrap';
        p.textContent = msg;
        const hint = document.createElement('p');
        hint.style.cssText = 'color:#aaa;font-size:12px;margin-top:24px';
        hint.textContent = 'Sorun devam ederse tarayıcı konsolunu (F12) açıp hata ' +
            "mesajlarını paylaşın. iOS/iPad'de Safari 17+ gerekli (ayarlar→Safari→" +
            'gelişmiş→WebCodecs feature flag enable).';
        wrap.append(h, p, hint);
        errorOverlay.appendChild(wrap);
        errorOverlay.style.display = 'block';
    }
    let framesRendered = 0;
    let bytesRecv = 0;
    let packetsRecv = 0;
    let decoderError = null;
    let showStats = true;
    let curtainActive = false;
    let muted = false;
    let lastPacketAt = Date.now();
    let recordingMediaRecorder = null;
    const userEmail = appState.userEmail ?? 'siz';
    const decoder = new H264Decoder(1920, 1080, (frame) => {
        if (ctx && !curtainActive) {
            ctx.drawImage(frame, 0, 0, canvas.width, canvas.height);
        }
        framesRendered++;
        frame.close();
    }, (e) => {
        decoderError = e.message;
        showError('Video decoder hatası: ' + e.message);
    });
    const videoReasm = new VideoReassembler((annexB, pts, isKey) => {
        try {
            decoder.decode(annexB, isKey, pts * 1000 / 90);
        }
        catch (e) {
            decoderError = String(e);
        }
    });
    const audioPlayer = new AudioPlayer();
    void audioPlayer.start();
    const bridge = new WsBridge();
    // Faz V: backend'in SHA-256 ile ürettiği 24-bit relay session ID
    // (pairing.relaySessionId). Host-agent aynı ID'yi packet header'ına yazar
    // — relay iki ucu eşleştirebilmek için tam aynı değeri görmeli.
    const relaySid24 = pairing.relaySessionId;
    const inputSender = new InputSender(bridge, relaySid24);
    const clipboardBroker = new ClipboardBroker(bridge, relaySid24);
    const fileSender = new FileSender(bridge, relaySid24);
    const signaling = new SignalingClient();
    signaling.setOnReplaced(() => {
        // Bir başka tab/client aynı device ID ile bağlandı → sonsuz reconnect
        // ping-pong olmaması için signaling-client kendi reconnect'ini durdurdu.
        // Kullanıcıya net mesaj göster.
        showError('Bu cihaz başka bir tab/pencereden signaling sunucusuna bağlandı, ' +
            'oturumun atıldı.\n\nBaşka açık PhotonRemote tab\'larını kapatıp ' +
            '🔄 Yeniden Bağlan butonuna bas.');
        reconnectBtn.style.display = 'inline-block';
    });
    // Faz 9: file-browse-response listener'ları (FileTransferPage register eder).
    const fileBrowseListeners = new Map();
    signaling.setOnMessage((msg) => {
        if (msg.type === 'chat' && typeof msg.text === 'string') {
            addChatEntry({
                sender: msg.sender ?? 'host',
                text: msg.text,
                at: new Date(msg.sentAt ?? new Date().toISOString()),
                own: false,
            });
        }
        else if (msg.type === 'file-browse-response'
            && typeof msg.correlationId === 'string') {
            const cb = fileBrowseListeners.get(msg.correlationId);
            if (cb) {
                fileBrowseListeners.delete(msg.correlationId);
                cb(msg);
            }
        }
    });
    // Faz 9: aktif session'ı global store'a yayınla — file-transfer/meeting
    // sayfaları bu nesneye erişerek RPC + file send yapar.
    activeSession.set({
        sessionId: pairing.sessionId,
        peerDeviceId: pairing.deviceId,
        signaling,
        fileSender,
    });
    // Active session'a file-browse helper iliştir (any field).
    activeSession.get().browse = async (path, timeoutMs = 12000) => {
        const correlationId = crypto.randomUUID();
        return new Promise((resolve, reject) => {
            const t = setTimeout(() => {
                fileBrowseListeners.delete(correlationId);
                reject(new Error('file-browse timeout'));
            }, timeoutMs);
            fileBrowseListeners.set(correlationId, (m) => {
                clearTimeout(t);
                resolve(m);
            });
            const sent = signaling.send({
                type: 'file-browse-request',
                sessionId: pairing.sessionId,
                correlationId,
                path: path ?? null,
            });
            if (!sent) {
                fileBrowseListeners.delete(correlationId);
                clearTimeout(t);
                reject(new Error('signaling kapalı'));
            }
        });
    };
    bridge.setOnPacket((bytes) => {
        bytesRecv += bytes.length;
        packetsRecv++;
        lastPacketAt = Date.now();
        const hdr = decodeHeader(bytes);
        if (!hdr)
            return;
        switch (hdr.channel) {
            case Channel.Video:
                videoReasm.process(bytes);
                break;
            case Channel.Audio: {
                const frame = parseAudioFrame(bytes.subarray(11));
                if (frame)
                    audioPlayer.push(frame);
                break;
            }
            case Channel.Clipboard:
                clipboardBroker.handleIncoming(bytes.subarray(11));
                break;
            default: break;
        }
    });
    // Faz V U8: iPad/mobile background'a çıkıp geri dönünce WS kopuyor.
    // visibilitychange + "Yeniden Bağlan" butonu ile aynı session'a reconnect.
    let controllerDeviceCached = null;
    const reconnectBtn = root.querySelector('#btn-reconnect');
    async function connectAll() {
        errorOverlay.style.display = 'none';
        reconnectBtn.style.display = 'none';
        try {
            if (!controllerDeviceCached) {
                controllerDeviceCached = await ensureWebControllerDevice();
            }
            try {
                await bridge.connect(relayUrl, pairing.sessionId, jwt, pairing.relaySessionId);
            }
            catch (e) {
                throw new Error(`Relay (${relayUrl}) bağlanamadı: ${e}`);
            }
            try {
                await signaling.connect(signalingUrl, jwt);
            }
            catch (e) {
                throw new Error(`Signaling (${signalingUrl}) bağlanamadı: ${e}`);
            }
            signaling.register(controllerDeviceCached.deviceId, '', ['input', 'v1']);
            const monitorIndex = pairing.monitorIndex;
            const sent = signaling.sendOffer({
                sessionId: pairing.sessionId,
                fromDeviceId: controllerDeviceCached.deviceId,
                toDeviceId: pairing.deviceId,
                monitorIndex,
            });
            if (!sent)
                throw new Error('Offer gönderilemedi.');
            lastPacketAt = Date.now(); // re-connected; idle reset
        }
        catch (e) {
            decoderError = String(e);
            showError(String(e) + '\n\n🔄 Yeniden Bağlan butonuna basarak tekrar dene.');
            reconnectBtn.style.display = 'inline-block';
            throw e;
        }
    }
    reconnectBtn.addEventListener('click', async () => {
        reconnectBtn.disabled = true;
        try {
            // Mevcut bağlantıları kapat (temiz state)
            try {
                bridge.close();
            }
            catch { }
            try {
                signaling.close();
            }
            catch { }
            await new Promise(r => setTimeout(r, 500));
            await connectAll();
        }
        catch {
            // connectAll zaten error overlay + reconnect btn visible bırakır
        }
        finally {
            reconnectBtn.disabled = false;
        }
    });
    // U8.7: iPad background/foreground cycle'ında iki ayrı sorun var:
    //   1) WS iOS tarafından kapatılıyor → reconnect gerekli (ws-dead path)
    //   2) WS alive kalıyor ama VideoDecoder state "closed"a düşüyor (iOS JS
    //      donması sırasında) → decoder reset + RequestKeyframe gerekli
    //      (ws-alive-but-stalled path)
    // visibility=visible olunca her iki path için de aksiyon al — kullanıcıya
    // banner göstermeden sessizce.
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            logEvent('info', `visibility=visible, ws=${bridge.isConnected}, signaling=${signaling.connected}`);
            // 1.5 saniye grace — hızlı tap'te gereksiz iş olmasın.
            setTimeout(() => {
                if (document.visibilityState !== 'visible')
                    return;
                const wsDead = !bridge.isConnected || !signaling.connected;
                if (wsDead) {
                    // Path 1: Full reconnect.
                    void (async () => {
                        try {
                            bridge.close();
                        }
                        catch { /* noop */ }
                        try {
                            signaling.close();
                        }
                        catch { /* noop */ }
                        await new Promise(r => setTimeout(r, 300));
                        try {
                            await connectAll();
                        }
                        catch {
                            reconnectBtn.style.display = 'inline-block';
                            showError('Bağlantı yeniden kurulamadı. 🔄 Yeniden Bağlan butonuna basarak tekrar dene.');
                        }
                    })();
                }
                else {
                    // Path 2: WS alive ama decoder muhtemelen stalled → reset + keyframe
                    // iste. Host-agent anında IDR gönderir, decoder yeni SPS/PPS ile
                    // configure olur, video devam eder.
                    try {
                        decoder.resetForResume();
                    }
                    catch { /* noop */ }
                    try {
                        signaling.sendRequestKeyframe(pairing.sessionId);
                    }
                    catch { /* noop */ }
                }
            }, 1500);
        }
        else {
            logEvent('info', 'visibility=hidden');
        }
    });
    void (async () => {
        try {
            try {
                await decoder.start();
            }
            catch (e) {
                throw new Error(`Video decoder başlatılamadı: ${e.message}`);
            }
            try {
                controllerDeviceCached = await ensureWebControllerDevice();
            }
            catch (e) {
                throw new Error(`Controller kaydı başarısız: ${e}. ` +
                    `Tarayıcınız IndexedDB destekliyor mu (private mod kapat)?`);
            }
            try {
                await bridge.connect(relayUrl, pairing.sessionId, jwt, pairing.relaySessionId);
            }
            catch (e) {
                throw new Error(`Relay (${relayUrl}) bağlanamadı: ${e}. ` +
                    `Tarayıcı WSS üzerinden bağlanır — kurumsal proxy/firewall ` +
                    `engelliyorsa network admin ile relay.photondownload.com ` +
                    `443/TCP'yi açtırın.`);
            }
            try {
                await signaling.connect(signalingUrl, jwt);
            }
            catch (e) {
                throw new Error(`Signaling (${signalingUrl}) bağlanamadı: ${e}. ` +
                    `JWT geçerli mi, signal.photondownload.com 443 açık mı?`);
            }
            signaling.register(controllerDeviceCached.deviceId, '', ['input', 'v1']);
            const monitorIndex = pairing.monitorIndex;
            const sent = signaling.sendOffer({
                sessionId: pairing.sessionId,
                fromDeviceId: controllerDeviceCached.deviceId,
                toDeviceId: pairing.deviceId,
                monitorIndex,
            });
            if (!sent) {
                throw new Error('Offer signaling\'e yollanamadı (WS açık değil).');
            }
        }
        catch (e) {
            decoderError = String(e);
            showError(String(e));
        }
    })();
    root.querySelector('#btn-close').addEventListener('click', () => {
        cleanup();
        // Faz 7: dynamic import yerine state.refresh() ile shell'e dön — vite
        // chunk warning'i temizler, mevcut akış aynı.
        appState.refresh();
    });
    root.querySelector('#btn-cad').addEventListener('click', () => {
        inputSender.keyDown(VK.Control);
        inputSender.keyDown(VK.Alt);
        inputSender.keyDown(VK.Delete);
        inputSender.keyUp(VK.Delete);
        inputSender.keyUp(VK.Alt);
        inputSender.keyUp(VK.Control);
    });
    root.querySelector('#btn-lock').addEventListener('click', () => {
        if (confirm('Uzak bilgisayar kilitlensin mi?')) {
            signaling.sendSystemAction(pairing.sessionId, 'Lock');
        }
    });
    root.querySelector('#btn-mute').addEventListener('click', (ev) => {
        muted = !muted;
        audioPlayer.setMuted(muted);
        ev.currentTarget.textContent = muted ? '🔈' : '🔇';
    });
    root.querySelector('#btn-curtain').addEventListener('click', (ev) => {
        curtainActive = !curtainActive;
        signaling.sendPrivacyCurtain(pairing.sessionId, curtainActive);
        ev.currentTarget.style.background = curtainActive ? '#555' : '';
        if (curtainActive && ctx) {
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
    });
    root.querySelector('#btn-stats').addEventListener('click', () => {
        showStats = !showStats;
        stats.style.display = showStats ? 'block' : 'none';
    });
    function toggleChatPanel(forceState) {
        const next = forceState ?? (chatPanel.style.display === 'none');
        chatPanel.style.display = next ? 'flex' : 'none';
        if (next) {
            setUnreadBadge(0);
            const inp = root.querySelector('#chat-input');
            if (inp)
                setTimeout(() => inp.focus(), 30);
            requestAnimationFrame(() => { chatLog.scrollTop = chatLog.scrollHeight; });
        }
    }
    root.querySelector('#btn-chat').addEventListener('click', () => {
        toggleChatPanel();
    });
    root.querySelector('#btn-chat-close').addEventListener('click', () => {
        toggleChatPanel(false);
    });
    // Faz V U8: Mobil klavye açma — hidden input'a focus → iOS/Android
    // sistem klavyesi çıkar → gelen key event'leri InputSender'a delege et.
    const kbdCapture = root.querySelector('#kbd-capture');
    root.querySelector('#btn-keyboard').addEventListener('click', () => {
        kbdCapture.focus();
        // iOS preventZoom
        kbdCapture.setAttribute('readonly', 'true');
        setTimeout(() => kbdCapture.removeAttribute('readonly'), 100);
    });
    kbdCapture.addEventListener('keydown', (e) => {
        const vk = inputSender.keyEventToVk(e);
        if (vk !== null) {
            e.preventDefault();
            inputSender.keyDown(vk);
        }
    });
    kbdCapture.addEventListener('keyup', (e) => {
        const vk = inputSender.keyEventToVk(e);
        if (vk !== null) {
            e.preventDefault();
            inputSender.keyUp(vk);
        }
    });
    // iOS/Android bazı karakter event'lerinde sadece `input` event çıkar
    // (ör. autosuggest kabul). textContent'i VK koduna çevir.
    kbdCapture.addEventListener('input', (e) => {
        const data = e.data;
        if (!data)
            return;
        for (const ch of data) {
            const code = ch.toUpperCase().charCodeAt(0);
            if (code >= 0x30 && code <= 0x5A) {
                // Digit or A-Z — Windows VK eşlemesi: 0-9=0x30-0x39, A-Z=0x41-0x5A
                inputSender.keyDown(code);
                inputSender.keyUp(code);
            }
            else if (ch === ' ') {
                inputSender.keyDown(0x20);
                inputSender.keyUp(0x20);
            }
        }
        kbdCapture.value = ''; // buffer temizle
    });
    root.querySelector('#btn-record').addEventListener('click', (ev) => {
        const btn = ev.currentTarget;
        if (recordingMediaRecorder) {
            recordingMediaRecorder.stop();
            recordingMediaRecorder = null;
            btn.textContent = '● Kayıt';
            btn.style.background = '';
            return;
        }
        try {
            // captureStream is not yet on TS CanvasCE lib types in some configs
            const stream = canvas.captureStream(30);
            const rec = new MediaRecorder(stream, { mimeType: 'video/webm' });
            const chunks = [];
            rec.ondataavailable = (e) => { if (e.data.size > 0)
                chunks.push(e.data); };
            rec.onstop = () => {
                const blob = new Blob(chunks, { type: 'video/webm' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `session-${pairing.sessionId.slice(0, 8)}-${Date.now()}.webm`;
                a.click();
                URL.revokeObjectURL(url);
            };
            rec.start(1000);
            recordingMediaRecorder = rec;
            btn.textContent = '■ Dur';
            btn.style.background = '#c00';
        }
        catch (e) {
            alert('Kayıt başlatılamadı: ' + e);
        }
    });
    root.querySelector('#sel-quality').addEventListener('change', (ev) => {
        const v = ev.currentTarget.value;
        const bps = v === 'high' ? 4_000_000 :
            v === 'balanced' ? 1_500_000 :
                v === 'smooth' ? 600_000 : 0;
        if (bps > 0)
            signaling.sendAdjustEncoder(pairing.sessionId, bps);
    });
    // Faz U9: paperclip + drag-drop + chat-input dosya gönderimi.
    async function sendFileViaChat(file) {
        const card = appendFileCard(file.name, file.size, /*own*/ true);
        try {
            const res = await fileSender.sendFile(file, (p) => {
                updateFileCard(card, p.bytesSent, p.totalBytes);
            });
            if (res.ok)
                markFileCardComplete(card, res.sha256);
            else
                markFileCardError(card, res.error ?? 'bilinmeyen');
        }
        catch (e) {
            markFileCardError(card, String(e));
        }
    }
    const fileInput = root.querySelector('#file-input');
    fileInput.addEventListener('change', async (ev) => {
        const inp = ev.currentTarget;
        const file = inp.files?.[0];
        if (file)
            await sendFileViaChat(file);
        inp.value = '';
    });
    // Drag-drop on chat panel
    ['dragenter', 'dragover'].forEach(evt => {
        chatPanel.addEventListener(evt, (e) => {
            const dt = e.dataTransfer;
            if (!dt || !Array.from(dt.types).includes('Files'))
                return;
            e.preventDefault();
            chatDropHint.style.display = 'flex';
        });
    });
    ['dragleave', 'dragend'].forEach(evt => {
        chatPanel.addEventListener(evt, (e) => {
            // dragleave fires for child crossings — only hide when leaving panel
            if (evt === 'dragleave') {
                const rt = e.relatedTarget;
                if (rt && chatPanel.contains(rt))
                    return;
            }
            chatDropHint.style.display = 'none';
        });
    });
    chatPanel.addEventListener('drop', async (e) => {
        e.preventDefault();
        chatDropHint.style.display = 'none';
        const files = e.dataTransfer?.files;
        if (!files || files.length === 0)
            return;
        for (const f of Array.from(files)) {
            await sendFileViaChat(f);
        }
    });
    const chatForm = root.querySelector('#chat-form');
    const chatInput = root.querySelector('#chat-input');
    // Auto-grow textarea
    chatInput.addEventListener('input', () => {
        chatInput.style.height = 'auto';
        chatInput.style.height = Math.min(chatInput.scrollHeight, 120) + 'px';
    });
    // Enter = send, Shift+Enter = newline
    chatInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
            e.preventDefault();
            chatForm.requestSubmit();
        }
    });
    chatForm.addEventListener('submit', (ev) => {
        ev.preventDefault();
        const text = chatInput.value.trim();
        if (!text)
            return;
        signaling.sendChat(pairing.sessionId, userEmail, text);
        addChatEntry({ sender: userEmail, text, at: new Date(), own: true });
        chatInput.value = '';
        chatInput.style.height = 'auto';
    });
    canvas.addEventListener('mousemove', (e) => {
        const rect = canvas.getBoundingClientRect();
        const x = Math.round((e.clientX - rect.left) * (canvas.width / rect.width));
        const y = Math.round((e.clientY - rect.top) * (canvas.height / rect.height));
        inputSender.mouseMove(x, y);
    });
    canvas.addEventListener('mousedown', (e) => {
        inputSender.mouseButton(e.button, true);
        canvas.focus();
    });
    canvas.addEventListener('mouseup', (e) => inputSender.mouseButton(e.button, false));
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
    canvas.addEventListener('wheel', (e) => {
        e.preventDefault();
        inputSender.mouseWheel(0, e.deltaY > 0 ? -120 : 120);
    }, { passive: false });
    canvas.addEventListener('keydown', (e) => {
        const vk = inputSender.keyEventToVk(e);
        if (vk !== null) {
            e.preventDefault();
            inputSender.keyDown(vk);
        }
    });
    canvas.addEventListener('keyup', (e) => {
        const vk = inputSender.keyEventToVk(e);
        if (vk !== null) {
            e.preventDefault();
            inputSender.keyUp(vk);
        }
    });
    const stopClipPoll = clipboardBroker.startPollLoop(800);
    let lastBytes = 0;
    let lastStatsAt = Date.now();
    let lastKfRequestAt = 0;
    const statsTimer = window.setInterval(() => {
        const now = Date.now();
        const dt = (now - lastStatsAt) / 1000;
        const dBytes = bytesRecv - lastBytes;
        const mbps = dt > 0 ? (dBytes * 8 / 1_000_000) / dt : 0;
        lastStatsAt = now;
        lastBytes = bytesRecv;
        const idleSec = (now - lastPacketAt) / 1000;
        let health;
        if (idleSec > 4)
            health = 'lost';
        else if (idleSec > 1.5)
            health = 'degraded';
        else
            health = 'good';
        healthBadge.textContent = health === 'good' ? '🟢' :
            health === 'degraded' ? '🟡' : '🔴';
        healthBadge.title = `bağlantı: ${health} (${idleSec.toFixed(1)}s idle)`;
        bitrateSpan.textContent = `${mbps.toFixed(2)} Mbps`;
        // Auto request-keyframe (health lost + 10s throttle)
        if (health === 'lost' && now - lastKfRequestAt > 10_000) {
            signaling.sendRequestKeyframe(pairing.sessionId);
            lastKfRequestAt = now;
        }
        stats.textContent = [
            `packets=${packetsRecv}`,
            `asm=${videoReasm.framesAssembled}`,
            `drop-gap=${videoReasm.droppedGaps}`,
            `rendered=${framesRendered}`,
            `audio dec=${audioPlayer.framesDecoded}/${audioPlayer.framesPlayed}`,
            `sig=${signaling.connected ? 'on' : 'off'}`,
            decoderError ? `ERR: ${decoderError}` : '',
        ].filter(Boolean).join(' • ');
    }, 500);
    function cleanup() {
        clearInterval(statsTimer);
        stopClipPoll();
        bridge.close();
        signaling.close();
        audioPlayer.close();
        recordingMediaRecorder?.stop();
        decoder.close().catch(() => { });
        activeSession.set(null);
    }
    function ensureDaySeparator(at) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayKey = at.toDateString();
        if (chatLastDay === dayKey)
            return;
        chatLastDay = dayKey;
        chatLastSender = null;
        let label;
        const at0 = new Date(at);
        at0.setHours(0, 0, 0, 0);
        const diffDays = Math.round((today.getTime() - at0.getTime()) / 86_400_000);
        if (diffDays === 0)
            label = 'Bugün';
        else if (diffDays === 1)
            label = 'Dün';
        else
            label = at.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
        const sep = document.createElement('div');
        sep.style.cssText =
            'align-self:center;margin:8px 0 6px 0;padding:2px 10px;font-size:10px;' +
                'font-weight:600;color:#aaa;background:#26262a;border-radius:10px';
        sep.textContent = label;
        chatLog.appendChild(sep);
    }
    function appendBubble(content, sender, at, own) {
        ensureDaySeparator(at);
        const grouped = chatLastSender === sender;
        chatLastSender = sender;
        const row = document.createElement('div');
        row.style.cssText = 'display:flex;flex-direction:column;max-width:78%;' +
            `margin-top:${grouped ? '1px' : '6px'};` +
            `align-self:${own ? 'flex-end' : 'flex-start'}`;
        if (!grouped && !own) {
            const senderEl = document.createElement('div');
            senderEl.style.cssText = 'font-size:11px;font-weight:600;color:#60a5fa;margin:0 4px 2px';
            senderEl.textContent = sender;
            row.appendChild(senderEl);
        }
        const bubble = document.createElement('div');
        bubble.style.cssText = 'padding:7px 11px;border-radius:14px;font-size:13px;' +
            'line-height:1.35;word-wrap:break-word;white-space:pre-wrap;' +
            (own
                ? 'background:#3b82f6;color:#fff;border-bottom-right-radius:4px'
                : 'background:#26262a;color:#eee;border-bottom-left-radius:4px');
        bubble.appendChild(content);
        const time = document.createElement('div');
        time.style.cssText = `font-size:10px;margin-top:3px;text-align:right;color:${own ? 'rgba(255,255,255,0.75)' : '#888'}`;
        time.textContent = at.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
        bubble.appendChild(time);
        row.appendChild(bubble);
        chatLog.appendChild(row);
        chatLog.scrollTop = chatLog.scrollHeight;
        if (!own) {
            if (chatPanel.style.display === 'none') {
                setUnreadBadge(chatUnreadCount + 1);
            }
        }
        return bubble;
    }
    function addChatEntry(e) {
        const text = document.createElement('div');
        text.textContent = e.text;
        appendBubble(text, e.sender, e.at, e.own);
    }
    function fmtSize(b) {
        if (b < 1024)
            return b + ' B';
        let v = b;
        const u = ['KB', 'MB', 'GB', 'TB'];
        let i = -1;
        do {
            v /= 1024;
            i++;
        } while (v >= 1024 && i < u.length - 1);
        return v.toFixed(1) + ' ' + u[i];
    }
    function appendFileCard(name, total, own) {
        const wrap = document.createElement('div');
        wrap.style.cssText = 'display:flex;align-items:center;gap:10px;min-width:200px';
        const icon = document.createElement('div');
        icon.style.cssText = 'font-size:22px;flex-shrink:0';
        icon.textContent = '📄';
        const right = document.createElement('div');
        right.style.cssText = 'flex:1;min-width:0';
        const nameEl = document.createElement('div');
        nameEl.style.cssText = 'font-weight:600;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap';
        nameEl.textContent = name;
        const status = document.createElement('div');
        status.style.cssText = `font-size:11px;color:${own ? 'rgba(255,255,255,0.85)' : '#aaa'};margin-top:1px`;
        status.textContent = `${fmtSize(total)} · gönderiliyor…`;
        const barWrap = document.createElement('div');
        barWrap.style.cssText = `height:4px;border-radius:2px;background:${own ? 'rgba(255,255,255,0.2)' : '#1a1a1c'};margin-top:6px;overflow:hidden`;
        const bar = document.createElement('div');
        bar.style.cssText = `height:100%;width:0%;background:${own ? '#fff' : '#3b82f6'};transition:width 0.2s`;
        barWrap.appendChild(bar);
        right.appendChild(nameEl);
        right.appendChild(status);
        right.appendChild(barWrap);
        wrap.appendChild(icon);
        wrap.appendChild(right);
        appendBubble(wrap, own ? userEmail : 'Uzak', new Date(), own);
        return { el: wrap, bar, status, total };
    }
    function updateFileCard(card, sent, total) {
        const pct = total > 0 ? (sent / total) * 100 : 0;
        card.bar.style.width = pct.toFixed(1) + '%';
        card.status.textContent = `${fmtSize(sent)} / ${fmtSize(total)} · %${pct.toFixed(0)}`;
    }
    function markFileCardComplete(card, sha) {
        card.bar.style.width = '100%';
        card.bar.style.background = '#22c55e';
        card.status.textContent = sha
            ? `${fmtSize(card.total)} · gönderildi ✓ (sha ${sha.slice(0, 8)})`
            : `${fmtSize(card.total)} · gönderildi ✓`;
    }
    function markFileCardError(card, err) {
        card.bar.style.background = '#e11d48';
        card.status.textContent = 'hata: ' + err;
        card.status.style.color = '#fca5a5';
    }
    return root;
}
